import { MarkdownProse } from "@/components/ui/markdown-prose";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { cn } from "@/lib/utils";

function isQuotedPersonaThought(text: string): boolean {
  const trimmed = text.trim();
  return (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("“") && trimmed.endsWith("”"))
  );
}

function stripQuotes(text: string): string {
  return text.trim().replace(/^["“]|["”]$/g, "");
}

const BASIS_LABELS: Record<string, string> = {
  rule: "Project rule",
  seen: "Visual observation",
  inferred: "Inferred",
};

export interface FindingDesignCauseItem {
  code?: string | null;
  title?: string | null;
  what?: string | null;
  why?: string | null;
  detail?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  basis?: string | null;
  screen?: string | null;
}

export interface FindingObservationProps {
  uiFact?: string | null;
  consequence?: string | null;
  what?: string | null;
  why?: string | null;
  basis?: string | null;
  screen?: string | null;
  breaksRule?: string | null;
  breaksFile?: string | null;
  description?: string | null;
  designCauses?: FindingDesignCauseItem[] | null;
  className?: string;
}

export function FindingObservation({
  uiFact,
  consequence,
  what,
  why,
  basis,
  screen,
  breaksRule,
  breaksFile,
  description,
  designCauses,
  className,
}: FindingObservationProps) {
  const hasUiFact = Boolean(uiFact && uiFact.trim().length > 0);
  const hasConsequence = Boolean(consequence && consequence.trim().length > 0);
  const hasWhat = Boolean(!hasUiFact && what && what.trim().length > 0);
  const hasWhy = Boolean(!hasConsequence && why && why.trim().length > 0);
  const hasRuleBroken = Boolean(breaksRule && breaksRule.trim().length > 0);
  const hasDesignCauses = Boolean(designCauses && designCauses.length > 0);

  const hasStructured =
    hasUiFact ||
    hasConsequence ||
    hasWhat ||
    hasWhy ||
    hasRuleBroken ||
    hasDesignCauses;

  const trimmedDesc = description?.trim() ?? "";
  const hasSeparateDescription = Boolean(
    trimmedDesc.length > 0 &&
      trimmedDesc !== uiFact?.trim() &&
      trimmedDesc !== consequence?.trim() &&
      trimmedDesc !== what?.trim() &&
      trimmedDesc !== why?.trim(),
  );

  if (!hasStructured && !hasSeparateDescription) {
    return null;
  }

  if (hasStructured) {
    const hasObservationSection = hasUiFact || hasWhat;
    const hasImpactSection = hasConsequence || hasWhy;

    return (
      <div
        className={cn(
          "rounded-lg border border-border bg-card p-5 space-y-4 max-w-3xl",
          className,
        )}
      >
        {hasUiFact && (
          <div className="space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">
              UI Fact
            </span>
            <p className="text-base leading-relaxed text-foreground">
              {uiFact}
            </p>
          </div>
        )}
        {hasWhat && (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                What the designer saw
              </span>
              {screen && (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {screen}
                </span>
              )}
              {basis && (
                <span className="text-xs text-muted-foreground">
                  · {BASIS_LABELS[basis] ?? basis}
                </span>
              )}
            </div>
            <p className="text-base leading-relaxed text-foreground">{what}</p>
          </div>
        )}
        {hasConsequence && (
          <div
            className={cn(
              "space-y-1",
              hasObservationSection && "border-t border-border/60 pt-3",
            )}
          >
            <span className="text-xs font-semibold text-muted-foreground">
              Consequence
            </span>
            <p className="text-base leading-relaxed text-foreground">
              {consequence}
            </p>
          </div>
        )}
        {hasWhy && (
          <div
            className={cn(
              "space-y-1",
              hasObservationSection && "border-t border-border/60 pt-3",
            )}
          >
            <span className="text-xs font-semibold text-muted-foreground">
              Why it matters
            </span>
            <p className="text-base leading-relaxed text-foreground">{why}</p>
          </div>
        )}
        {hasRuleBroken && (
          <div
            className={cn(
              "space-y-1",
              (hasObservationSection || hasImpactSection) &&
                "border-t border-border/60 pt-3",
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">
                Rule broken
              </span>
              {breaksFile && (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                  {breaksFile}
                </span>
              )}
            </div>
            <p className="text-base font-medium leading-relaxed text-foreground">
              “{breaksRule}”
            </p>
          </div>
        )}
        {hasSeparateDescription && description && (
          <div
            className={cn(
              "space-y-1.5",
              (hasObservationSection || hasImpactSection || hasRuleBroken) &&
                "border-t border-border/60 pt-3",
            )}
          >
            <span className="text-xs font-semibold text-muted-foreground">
              {isQuotedPersonaThought(description)
                ? "Persona thought"
                : "Additional Context"}
            </span>
            {isQuotedPersonaThought(description) ? (
              <PersonaVoice size="base" className="leading-relaxed">
                “{stripQuotes(description)}”
              </PersonaVoice>
            ) : (
              <MarkdownProse
                content={description}
                className="text-base leading-relaxed text-foreground"
              />
            )}
          </div>
        )}
        {hasDesignCauses && designCauses && (
          <div
            className={cn(
              "space-y-2.5",
              (hasObservationSection ||
                hasImpactSection ||
                hasRuleBroken ||
                hasSeparateDescription) &&
                "border-t border-border/60 pt-3",
            )}
          >
            {designCauses.map((cause, idx) => {
              const hasStructuredCause = Boolean(cause.what || cause.why);
              return (
                <div
                  key={`${cause.code ?? "dc"}-${idx}`}
                  className="rounded-md border border-border/70 bg-muted/25 p-3.5 space-y-1.5"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-foreground">
                      Why the UI caused this (Designer)
                    </span>
                    {cause.code && (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                        {cause.code}
                      </span>
                    )}
                    {cause.screen && (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                        {cause.screen}
                      </span>
                    )}
                  </div>
                  {cause.title && hasStructuredCause && (
                    <p className="text-sm font-medium text-foreground">
                      {cause.title}
                    </p>
                  )}
                  {hasStructuredCause ? (
                    <div className="space-y-1 text-sm leading-relaxed">
                      {cause.what && (
                        <p className="text-foreground">{cause.what}</p>
                      )}
                      {cause.why && (
                        <p className="text-muted-foreground">{cause.why}</p>
                      )}
                    </div>
                  ) : (
                    cause.detail && (
                      <p className="text-sm leading-relaxed text-foreground">
                        {cause.detail}
                      </p>
                    )
                  )}
                  {cause.breaksRule && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        Breaks rule: “{cause.breaksRule}”
                      </span>
                      {cause.breaksFile && (
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                          {cause.breaksFile}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (isQuotedPersonaThought(description || "")) {
    return (
      <div className={cn("max-w-3xl", className)}>
        <PersonaVoice size="base" className="leading-relaxed">
          “{stripQuotes(description || "")}”
        </PersonaVoice>
      </div>
    );
  }

  return (
    <div className={cn("max-w-3xl", className)}>
      <MarkdownProse
        content={description || ""}
        className="text-base leading-relaxed text-foreground"
      />
    </div>
  );
}
